import { useEffect, useMemo, useRef, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'
import { monadClient } from '../../api/client'
import { attributesQuery, typeQuery } from '../../api/typeMetadata'
import type { CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import type { FilterExpr } from '../../api/schemas'
import { CreateRecordModal } from '../../records/CreateRecordModal'
import { resolvePrimaryAttribute } from '../../api/primaryAttribute'
import { ChipLabel } from '../ChipLabel'
import { Button } from '../Button'

/**
 * SEN-710 — registry-driven entity-reference editor.
 *
 * A kenning-labeled record selector over the attribute's config.target_type_id
 * (never shows a UUID — monad-never-show-uuid), with a universal '+ Create new'
 * row (monad-always-offer-create) that opens the standalone CreateRecordModal —
 * the minimal editor promoted from the old inline form (Task 1). Recursion is
 * depth-tracked so nested modals stack at increasing z-index levels.
 *
 * Reverse attributes (config.is_reverse) are read-only — the reverse side is
 * resolved at query time (SEN-707); the editor declines to mount.
 *
 * SEN-723: search input is debounced 250ms and issues getMrl with a contains
 * FilterExpr on the target type's display attribute (name → first text → first).
 * SEN-726: getMrl rejection renders ref-load-error + Retry (not "No matches").
 *
 * Wave B / CEO round 1 Rule 3 — one chip seam, every surface:
 * Picker dropdown rows and the selected-value trigger both render ChipLabel
 * (icon + colour + kenning) via a single POST /records/chips call for ALL
 * visible ids + the currently selected id (one fetch, 60s stale). Falls back
 * to plain kenning while chips are loading or absent — no flash of broken.
 */

/** Exported so the strip's reference-value chip label resolves kennings via
 *  the SAME page size and the editor's UNFILTERED query key
 *  ['mrl-kennings', slug, targetTypeId, ''] — FilterChips appends the
 *  matching '' (q='') key element so both readers share one cache entry. */
export const MRL_PAGE = 200

type CandidateAttribute = {
  attribute_id: string
  slug: string
  name: string
  type: string
  is_unique?: boolean
}

type EntityReferenceEditorProps = CellEditorProps & {
  /** Delegate creation to a workflow that stages its own persistence. When
   *  absent, the editor keeps its normal immediate CreateRecordModal path. */
  onCreateNew?: () => void
  /** Human type label served by the registry for the delegated create row. */
  createNewTypeLabel?: string
}

function candidateValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not reported'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return 'Reported value'
}

/** Build a contains FilterExpr for the display attribute of a type.
 *  Uses the shared resolvePrimaryAttribute heuristic: name-named → first text → first.
 *  Returns undefined when q is empty or no attributes are available. */
function buildSearchFilter(
  attrs: Array<{ attribute_id: string; name: string; type: string }> | undefined,
  typeId: string,
  q: string,
): FilterExpr | undefined {
  if (!q.trim() || !attrs || attrs.length === 0) return undefined
  const displayAttr = resolvePrimaryAttribute(attrs)
  if (!displayAttr) return undefined
  return {
    clause: {
      path: [{ type_id: typeId, attribute_id: displayAttr.attribute_id }],
      mode: 'must',
      constraints: [{ field: 'value', operator: 'contains', value: q.trim() }],
    },
  }
}

export function EntityReferenceEditor({
  value, onCommit, onCancel, onCreateNew, createNewTypeLabel, config, context, slug,
}: EntityReferenceEditorProps) {
  const targetTypeId = (config?.target_type_id as string | undefined) ?? ''
  const showCandidateContext = config?.candidate_context === true
  const isReverse = (config as { is_reverse?: boolean } | undefined)?.is_reverse === true
  const unusable = isReverse || !slug || !targetTypeId
  // Reverse side / missing metadata: decline to edit (read-only) — via effect,
  // not at render time, so React state stays clean.
  useEffect(() => { if (unusable) onCancel() }, [unusable, onCancel])

  const [query, setQuery] = useState('')
  // debouncedQ is the query value actually sent to getMrl (250ms lag)
  const [debouncedQ, setDebouncedQ] = useState('')
  const [creating, setCreating] = useState(false)
  // Ref mirror for onOpenChange: mounting the nested modal steals focus and
  // Radix dismisses the popover in the SAME tick setCreating(true) lands —
  // the closure would still see creating=false and cancel-unmount the modal.
  const creatingRef = useRef(false)
  creatingRef.current = creating
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Declared per-context capabilities — no inline which-context-am-I tests.
  const b = editorBehavior(context)
  const [open, setOpen] = useState(false)

  // Clear any pending debounce on unmount so no state update fires after teardown
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  function handleSearchChange(next: string) {
    setQuery(next)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQ(next), 250)
  }

  // Fetch target type's attributes to resolve the display attribute for search
  const { data: targetAttrs } = useQuery({
    ...attributesQuery(slug!, targetTypeId),
    enabled: !unusable,
  })
  const { data: targetType } = useQuery({
    ...typeQuery(slug!, targetTypeId),
    enabled: !unusable && showCandidateContext,
  })

  const searchFilter = buildSearchFilter(
    targetAttrs as Array<{ attribute_id: string; name: string; type: string }> | undefined,
    targetTypeId,
    debouncedQ,
  )

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['mrl-kennings', slug, targetTypeId, debouncedQ],
    queryFn: () => monadClient.getMrl(slug!, targetTypeId, MRL_PAGE, 0, searchFilter),
    enabled: !unusable,
    staleTime: 60_000,
  })

  if (unusable) return null

  const kennings = data?.record_kennings ?? {}
  const ids = data?.record_ids ?? []
  const candidateAttributes = useMemo(() => {
    if (!showCandidateContext) return []
    const attrs = (targetAttrs ?? []) as CandidateAttribute[]
    const identities = attrs.filter(attribute => attribute.is_unique === true)
    const identityIds = new Set(identities.map(attribute => attribute.attribute_id))
    const bySlug = new Map(attrs.map(attribute => [attribute.slug, attribute]))
    const kenningPath = ((targetType as { kenning_path?: string[] } | undefined)?.kenning_path ?? [])
    const distinguishing = kenningPath
      .map(slug => bySlug.get(slug))
      .find((attribute): attribute is CandidateAttribute => Boolean(
        attribute && !identityIds.has(attribute.attribute_id),
      ))
    return distinguishing ? [...identities, distinguishing] : identities
  }, [showCandidateContext, targetAttrs, targetType])
  const candidateAttributeIds = candidateAttributes.map(attribute => attribute.attribute_id)
  const candidateAttributeKey = candidateAttributeIds.join(',')
  const { data: candidateData } = useQuery({
    queryKey: ['reference-candidate-context', slug, targetTypeId, ids, candidateAttributeKey],
    queryFn: () => monadClient.getValues(
      slug!, targetTypeId, ids, candidateAttributeIds,
    ),
    enabled: !unusable && showCandidateContext
      && ids.length > 0 && candidateAttributeIds.length > 0,
    staleTime: 60_000,
  })
  const candidateValues = candidateData?.values ?? {}
  const currentId =
    value && typeof value === 'object' && 'target_record_id' in (value as object)
      ? (value as { target_record_id?: string }).target_record_id
      : typeof value === 'string' ? value : undefined

  // Chip resolution — one batch call for the visible page ids + currently selected id.
  // Query key uses a stable sort of all ids (+ debouncedQ so it re-fetches with new page).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const allIdsForChips = useMemo(() => {
    const set = new Set(ids)
    if (currentId) set.add(currentId)
    return Array.from(set)
  }, [ids, currentId])

  // Stable cache key: sorted ids joined. Keeps the key deterministic regardless of order.
  const chipsQueryKey = allIdsForChips.length > 0 ? [...allIdsForChips].sort().join(',') : null

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: chipsData } = useQuery({
    queryKey: ['chips', slug, targetTypeId, debouncedQ, chipsQueryKey],
    queryFn: () => monadClient.getChips(slug!, allIdsForChips),
    enabled: !unusable && allIdsForChips.length > 0,
    staleTime: 60_000,
  })
  // chips: {record_id: {kenning, icon, color}} — empty when still loading/absent
  const chips = chipsData?.chips ?? {}

  function handleCommit(next: unknown) {
    if (b.controlledOpen) setOpen(false)
    onCommit(next)
  }

  function handleCreateNew() {
    if (onCreateNew) {
      if (b.controlledOpen) setOpen(false)
      onCreateNew()
      return
    }
    setCreating(true)
  }

  // Trigger label: chip when available, plain kenning fallback while loading
  function renderTriggerLabel() {
    if (!currentId) return <span className="text-muted-foreground">—</span>
    const chip = chips[currentId]
    if (chip) {
      return <ChipLabel kenning={chip.kenning} icon={chip.icon} color={chip.color} />
    }
    // Fallback: plain kenning from MRL while chips load
    return <span className="truncate">{kennings[currentId] ?? '…'}</span>
  }

  return (
    <>
      <Popover.Root
        open={b.controlledOpen ? open : undefined}
        defaultOpen={!b.controlledOpen}
        onOpenChange={(o) => {
          if (b.controlledOpen) setOpen(o)
          // While the nested create modal is up, the popover dismissing (the
          // modal's focus-steal counts as an outside interaction) must NOT
          // cancel — cancelling unmounts the editor AND the modal with it.
          if (!o && b.dismissCancels && !creatingRef.current) onCancel()
        }}
      >
        <Popover.Trigger asChild>
          <button
            data-testid="cell-editor"
            className={`flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none ${controlChrome(context)}`}
            onClick={b.controlledOpen ? () => setOpen(true) : undefined}
          >
            {renderTriggerLabel()}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="z-[100] w-[max(var(--radix-popover-trigger-width),16rem)] overflow-hidden rounded-float border border-border bg-background shadow-float"
          >
            <div className="border-b border-border px-2 py-1.5">
              <input
                autoFocus
                data-testid="ref-search"
                value={query}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel() } }}
                placeholder="Search records…"
                className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="max-h-60 overflow-auto p-1">
              {/* SEN-726: explicit error state — never conflates with "No matches" */}
              {isError && (
                <div className="px-2 py-1.5">
                  <div data-testid="ref-load-error" className="text-sm text-destructive">
                    Failed to load records.
                  </div>
                  <Button variant="link"
                    data-testid="ref-retry"
                    onClick={() => void refetch()}
                    className="mt-1 text-xs text-muted-foreground"
                  >
                    Retry
                  </Button>
                </div>
              )}
              {!isError && isLoading && <div className="px-2 py-1.5 text-sm text-muted-foreground">Loading…</div>}
              {!isError && ids.map((rid) => {
                const chip = chips[rid]
                return (
                  <Button variant="menuItem" fullWidth
                    key={rid}
                    onClick={() => handleCommit(rid)}
                    className="flex items-start gap-1.5"
                  >
                    <span className="grid min-w-0 gap-0.5">
                      {chip
                        ? <ChipLabel kenning={chip.kenning} icon={chip.icon} color={chip.color} />
                        : <span className="truncate">{kennings[rid] || '(unnamed)'}</span>
                      }
                      {showCandidateContext && candidateAttributes.length > 0 && (
                        <span data-testid={`ref-candidate-context-${rid}`}
                          className="flex min-w-0 flex-wrap gap-x-2 text-xs text-muted-foreground">
                          {candidateAttributes.map(attribute => (
                            <span key={attribute.attribute_id} className="truncate">
                              {attribute.name}: {candidateValue(
                                candidateValues[rid]?.[attribute.attribute_id],
                              )}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </Button>
                )
              })}
              {!isError && !isLoading && ids.length === 0 && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">No matches</div>
              )}
              {context !== 'filter' && config?.allow_create !== false && (
                <Button variant="menuItem" fullWidth
                  data-testid="ref-create-new"
                  onClick={handleCreateNew}
                  className="flex items-center gap-1.5 text-primary"
                >
                  <span className="text-base leading-none">+</span>{' '}
                  Create new{createNewTypeLabel ? ` ${createNewTypeLabel}` : ''}
                </Button>
              )}
              {/* SEN-779 — Remove: the reference editor's UI onto the generic
                  clear seam. Visible only when a value is set; commits null,
                  which set_value treats as null-as-archive (kind-agnostic). */}
              {currentId && (
                <Button variant="menuItem" fullWidth
                  data-testid="ref-clear"
                  onClick={() => handleCommit(null)}
                  className="flex items-center gap-1.5 text-muted-foreground"
                >
                  <span className="text-base leading-none">×</span> Remove
                </Button>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {creating && !onCreateNew && (
        <CreateRecordModal
          slug={slug!}
          typeId={targetTypeId}
          depth={1}
          onCreated={(rid) => { setCreating(false); onCommit(rid) }}
          onClose={() => {
            setCreating(false)
            // In cell context the popover already dismissed (focus-steal) —
            // exit the edit cleanly instead of leaving a ghost editor.
            if (b.dismissCancels) onCancel()
          }}
        />
      )}
    </>
  )
}
